import React, { JSX, useCallback, useState, useTransition } from 'react';

import { Text } from '@videoblocks/design-system';
import { Sparkles } from '@videoblocks/react-icons';

import { useVoiceoverContext } from '../_context/VoiceoverContext';
import {
    CloneMyVoiceState,
    TAB_TO_EVENT_TYPE,
    Tabs,
    TextToSpeechState,
    useVoiceoverTabContext,
} from '../_context/VoiceoverTabContext';
import Modal from './Modal';

export interface CreditConfirmationModalProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    creditCost: number;
    buttonText: string;
    generateRequestBody: () => BodyInit;
    onSuccess?: () => void;
}

const pluralize = (count: number, singular: string, plural?: string) => {
    if (count === 1) return singular;
    return plural || `${singular}s`;
};

const CreditConfirmationModal = ({
    isOpen,
    setIsOpen,
    creditCost,
    buttonText,
    generateRequestBody,
    onSuccess,
}: CreditConfirmationModalProps): JSX.Element | null => {
    const [isPending, startTransition] = useTransition();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const {
        credits,
        CreditsApi: { refreshCredits },
    } = useVoiceoverContext();
    const { activeTabState, updateState, activeTab } = useVoiceoverTabContext();
    const { aiContentId, lastAiContentId } = activeTabState;

    const handleGenerateAudio = () => {
        const voiceId =
            (activeTabState as TextToSpeechState).selectedVoice ||
            (activeTabState as CloneMyVoiceState).generatedVoiceId;

        const eventType = TAB_TO_EVENT_TYPE[activeTab];

        startTransition(async () => {
            try {
                const requestUrl = {
                    [Tabs.TextToSpeech]: '/api/voiceover/text-to-speech',
                    [Tabs.CloneMyVoice]: '/api/voiceover/text-to-speech',
                    [Tabs.VoiceChanger]: '/api/voiceover/speech-to-speech',
                }[activeTab];

                const body = generateRequestBody();

                const headers: HeadersInit = {
                    'X-AI-Content-Voiceover-Type': eventType,
                };

                if (typeof body === 'string') headers['Content-Type'] = 'application/json';

                // information we need to send from client to server for event emissions
                if (aiContentId) headers['X-AI-Content-Id'] = aiContentId;
                if (lastAiContentId) headers['X-Last-AI-Content-Id'] = lastAiContentId;

                const res = await fetch(requestUrl, {
                    method: 'POST',
                    headers,
                    body,
                });

                if (!res.ok) {
                    throw new Error(`HTTP Error: status ${res.status}`);
                }

                const blob = await res.blob();
                const url = URL.createObjectURL(blob);

                // server sent back the new content id as a response header
                const newAiContentId = res.headers.get('X-AI-Content-Id');

                updateState({
                    generatedAudioUrl: url,
                    generatedAudioVoiceId: voiceId,
                    // cycle the current aiContentId to lastAiContentId and set the new one
                    ...(newAiContentId && {
                        lastAiContentId: aiContentId,
                        aiContentId: newAiContentId,
                    }),
                });

                onSuccess?.();
                setIsOpen(false);
            } catch (err) {
                setErrorMessage('Voiceover service is temporarily unavailable. Please try again later.');
            } finally {
                void refreshCredits();
            }
        });
    };

    const remainingCredits = credits - creditCost;
    const hasRemainingCredits = remainingCredits >= 0;

    if (!hasRemainingCredits) {
        return null;
    }

    return (
        <Modal
            title={`Generate this voiceover for ${creditCost} ${pluralize(creditCost, 'credit')}?`}
            confirmDisabled={isPending}
            primaryActionText={
                <div className="flex flex-row gap-x-1">
                    <Sparkles className="w-4 h-4 fill-black" />
                    <span>{isPending ? 'Generating...' : buttonText}</span>
                </div>
            }
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onConfirm={() => {
                handleGenerateAudio();
            }}
            body={
                <div className="mt-4 flex flex-col gap-4">
                    <Text fontWeight="regular">
                        You&apos;ll have {remainingCredits} {pluralize(remainingCredits, 'credit')} remaining.
                    </Text>

                    {errorMessage && (
                        <Text fontWeight="regular" className="text-red-400">
                            {errorMessage}
                        </Text>
                    )}
                </div>
            }
        />
    );
};

export default CreditConfirmationModal;
